/**
 * -------------------------------------------------------------------------------------------------------------------
 * UTILITIES
 * -------------------------------------------------------------------------------------------------------------------
 */

/**
 * @param {String} nodes - ClassList with DOM reference
 * @param {HTMLElement} parent - Parent of nodes
 * @returns {Array} Array of nodes in DOM
 */
const $Qll = (
  nodes,
  parent
) => [...(parent || document).querySelectorAll(nodes)]

/**
 *
 * @param {HTMLElement} node - ClassName with DOM reference
 * @param {HTMLElement} parent - Parent of nodes
 * @returns {Node} Node in DOM
 */
const $Q = (node, parent) => (parent || document).querySelector(node)

/**
 * -------------------------------------------------------------------------------------------------------------------
 * COMPONENTS
 * -------------------------------------------------------------------------------------------------------------------
 */

/**
 * Dropdown open scope
 */
function dropdownScope () {
  const marketElements = $Qll('.localization-dropdown-js')
  
  marketElements.forEach((mainDropdown) => {
    const mainButton = $Q('.localization-btn-js', mainDropdown)
  
    mainButton.addEventListener('click', () => {
      mainDropdown.classList.toggle('open')
    })
  
    document.addEventListener('click', (event) => {
      if (!mainDropdown.contains(event.target)) {
        mainDropdown.classList.remove('open')
        subDropdowns.forEach((subDropdown) => {
          subDropdown.classList.remove('open')
        })
      }
    })
  })
}

/**
 * Product card image gallery — arrow navigation + boundary handling.
 * Mobile swipe is handled natively via CSS scroll-snap on the slides container.
 */
function initProductCardGallery (figure) {
  if (figure.dataset.galleryReady === 'true') return
  figure.dataset.galleryReady = 'true'

  const slides = $Q('[data-product-card-slides]', figure)
  const prevBtn = $Q('[data-product-card-nav="prev"]', figure)
  const nextBtn = $Q('[data-product-card-nav="next"]', figure)

  if (!slides || (!prevBtn && !nextBtn)) return

  const scrollByOne = (direction) => {
    const slide = $Q('.product-card__slide', slides)
    const step = slide ? slide.getBoundingClientRect().width : slides.clientWidth
    const max = slides.scrollWidth - slides.clientWidth
    const current = slides.scrollLeft

    let target
    let behavior = 'smooth'
    if (direction === 1) {
      if (current >= max - 1) {
        target = 0
        behavior = 'instant'
      } else {
        target = current + step
      }
    } else {
      if (current <= 1) {
        target = max
        behavior = 'instant'
      } else {
        target = current - step
      }
    }

    slides.scrollTo({ left: target, behavior })
  }

  if (prevBtn) {
    prevBtn.addEventListener('click', (event) => {
      event.preventDefault()
      event.stopPropagation()
      scrollByOne(-1)
    })
  }

  if (nextBtn) {
    nextBtn.addEventListener('click', (event) => {
      event.preventDefault()
      event.stopPropagation()
      scrollByOne(1)
    })
  }
}

function productCardGalleries () {
  $Qll('[data-product-card-figure]').forEach(initProductCardGallery)

  // Re-init for AJAX-injected cards (facet filters, pagination, quick view, etc.)
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return
        if (node.matches && node.matches('[data-product-card-figure]')) {
          initProductCardGallery(node)
        }
        if (node.querySelectorAll) {
          node.querySelectorAll('[data-product-card-figure]').forEach(initProductCardGallery)
        }
      })
    })
  })

  observer.observe(document.body, { childList: true, subtree: true })
}

/**
 * -------------------------------------------------------------------------------------------------------------------
 * INVOKES
 * -------------------------------------------------------------------------------------------------------------------
 */

function initInfiniteScroll () {
  $Qll('[data-infinite-scroll]').forEach((root) => {
    if (root.dataset.infiniteReady === 'true') return
    root.dataset.infiniteReady = 'true'

    const list = $Q('[data-infinite-scroll-list]', root)
    const paginationWrap = $Q('[data-infinite-scroll-pagination]', root)
    if (!list || !paginationWrap) return

    let nextLink = paginationWrap.querySelector('a[rel="next"]')
    if (!nextLink) {
      paginationWrap.style.display = 'none'
      return
    }

    paginationWrap.style.display = 'none'

    const sentinel = document.createElement('div')
    sentinel.setAttribute('aria-hidden', 'true')
    sentinel.style.height = '1px'
    paginationWrap.parentElement.insertBefore(sentinel, paginationWrap)

    let loading = false
    let nextUrl = nextLink.href

    const showLoading = (state) => {
      sentinel.style.padding = state ? '2rem 0' : '0'
      sentinel.textContent = state ? '…' : ''
      sentinel.style.textAlign = 'center'
      sentinel.style.color = 'rgb(var(--text-color) / .5)'
    }

    const loadNext = async () => {
      if (loading || !nextUrl) return
      loading = true
      showLoading(true)

      try {
        const response = await fetch(nextUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        if (!response.ok) throw new Error('Bad response: ' + response.status)
        const html = await response.text()
        const doc = new DOMParser().parseFromString(html, 'text/html')

        const newList = doc.querySelector('[data-infinite-scroll-list]')
        if (newList) {
          // Strip the reveal-on-scroll attribute — that initial-load animation
          // already fired, so any new cards with it would be stuck at opacity 0.
          newList.querySelectorAll('[reveal-on-scroll="true"]').forEach((el) => {
            el.removeAttribute('reveal-on-scroll')
          })

          const fragment = document.createDocumentFragment()
          while (newList.firstChild) fragment.appendChild(newList.firstChild)
          list.appendChild(fragment)

          // Re-init gallery for any new product cards that were just appended
          list.querySelectorAll('[data-product-card-figure]').forEach(initProductCardGallery)
        }

        const newPagination = doc.querySelector('[data-infinite-scroll-pagination]')
        const newNextLink = newPagination && newPagination.querySelector('a[rel="next"]')
        nextUrl = newNextLink ? newNextLink.href : null

        if (!nextUrl) {
          observer.disconnect()
          sentinel.remove()
          return
        }
      } catch (err) {
        // On error, restore visible pagination so the user has a fallback
        observer.disconnect()
        paginationWrap.style.display = ''
        sentinel.remove()
        return
      } finally {
        loading = false
        showLoading(false)
      }
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) loadNext()
    }, { rootMargin: '800px 0px' })

    observer.observe(sentinel)
  })
}

function initCustom () {
  dropdownScope()
  productCardGalleries()
  initInfiniteScroll()
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCustom)
} else {
  initCustom()
}